import {
  off,
  onDisconnect,
  onValue,
  ref,
  remove,
  serverTimestamp,
  set,
  update,
  type DataSnapshot,
  type Database,
} from "firebase/database";
import { getFirebaseDatabase } from "./firebase";
import { colorForUid } from "./cursorColor";
import type {
  CollaboratorState,
  PresenceCursor,
  PresenceDragging,
  PresenceEntry,
  PresenceSelection,
} from "./sharedRoomTypes";
import type { AuthUser } from "./authClient";
import { captureException } from "./telemetry";

/**
 * RTDB-backed presence layer for multiplayer-shared rooms.
 *
 * Lifecycle (per local user):
 *   - {@link initPresence}({roomId, user}) once when entering a shared
 *     room. Writes the initial entry and queues an `onDisconnect.remove`
 *     so a browser crash never leaks a ghost cursor.
 *   - {@link setCursor}, {@link setSelection}, {@link setDragging} during
 *     editing. Cursor + drag are throttled to one write per
 *     animation frame (~30-60 Hz, capped at 33 ms — Figma's pace).
 *   - {@link teardownPresence} on leave.
 *
 * Receivers use {@link subscribePresence} to get a normalised
 * `Map<uid, CollaboratorState>` aggregating multi-tab entries per user.
 */

interface SessionState {
  roomId: string;
  uid: string;
  tabId: string;
  displayName: string;
  photoURL: string | null;
  color: string;
  /** Latest cursor sampled but not yet flushed. `undefined` means
   *  "nothing pending"; `null` means "explicitly clear the cursor". */
  pendingCursor: PresenceCursor | null | undefined;
  /** Latest dragging payload sampled but not yet flushed. */
  pendingDragging: PresenceDragging | null | undefined;
  /** Latest selection sampled but not yet flushed. */
  pendingSelection: PresenceSelection | null | undefined;
  /** Whether we have a cursor/dragging frame queued for flush. */
  flushScheduled: boolean;
  /** Last time (ms) we flushed cursor/drag. Used to gate to ~30 Hz. */
  lastFlushAt: number;
  /** Whether we already did the initial write. Prevents duplicate
   *  initial set when initPresence is called twice in StrictMode. */
  initialized: boolean;
  /** Latched once we've already logged a write failure for this
   *  session, so the rAF flush loop doesn't spam the console. */
  warnedWriteFailed: boolean;
  /** RTDB ref strings so we can repeat-set safely. */
  entryRef: ReturnType<typeof ref> | null;
  db: Database | null;
}

let session: SessionState | null = null;

const TARGET_INTERVAL_MS = 33; // ~30 Hz wire rate

/** Lightweight tab id so multi-tab users get distinct presence entries. */
function newTabId(): string {
  return `t_${Math.random().toString(36).slice(2, 10)}`;
}

/** Path: `presence/{roomId}/{uid}/{tabId}`. */
function presenceKey(roomId: string, uid: string, tabId: string): string {
  return `presence/${roomId}/${uid}/${tabId}`;
}

/** Initialise presence for the local user. Idempotent — calling twice
 *  with the same roomId/uid pair is safe. */
export function initPresence(input: {
  roomId: string;
  user: AuthUser;
}): boolean {
  const db = getFirebaseDatabase();
  if (!db) return false;

  // Re-init for a different room: tear down the prior session first.
  if (session && (session.roomId !== input.roomId || session.uid !== input.user.uid)) {
    teardownPresence();
  }
  if (session?.initialized) return true;

  const tabId = newTabId();
  const displayName =
    input.user.displayName ?? input.user.email ?? "Guest";
  const color = colorForUid(input.user.uid);
  const entry: PresenceEntry = {
    uid: input.user.uid,
    tabId,
    displayName,
    photoURL: input.user.photoURL,
    color,
    cursor: null,
    selection: null,
    dragging: null,
    lastSeen: 0, // serverTimestamp on write
  };

  const entryRef = ref(db, presenceKey(input.roomId, input.user.uid, tabId));

  session = {
    roomId: input.roomId,
    uid: input.user.uid,
    tabId,
    displayName,
    photoURL: input.user.photoURL,
    color,
    // `undefined` (not `null`) so the first flush doesn't write a
    // bogus `cursor: null` over the initial entry's own `cursor: null`.
    pendingCursor: undefined,
    pendingDragging: undefined,
    pendingSelection: undefined,
    flushScheduled: false,
    lastFlushAt: 0,
    initialized: true,
    warnedWriteFailed: false,
    entryRef,
    db,
  };

  // Queue cleanup BEFORE writing the entry so a connection drop between
  // the two ops still leaves the data clean (Firebase docs: "the
  // disconnect operation should be queued before the user is marked
  // online to avoid race conditions").
  void onDisconnect(entryRef).remove();
  set(entryRef, { ...entry, lastSeen: serverTimestamp() }).catch((err) => {
    logWriteFailure("initial presence write", err);
  });

  return true;
}

/** Throttle the actual RTDB write to one per animation frame. */
function scheduleFlush(): void {
  if (!session) return;
  if (session.flushScheduled) return;
  session.flushScheduled = true;
  // requestAnimationFrame coalesces multiple `setCursor` calls in the
  // same frame to a single payload; the `since`-check inside `flush`
  // additionally caps writes at ~30 Hz.
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(flush);
  } else {
    setTimeout(flush, TARGET_INTERVAL_MS);
  }
}

function flush(): void {
  if (!session) return;
  const now =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  if (now - session.lastFlushAt < TARGET_INTERVAL_MS) {
    // Too soon — re-schedule for next frame so we still flush.
    session.flushScheduled = false;
    scheduleFlush();
    return;
  }
  session.flushScheduled = false;
  session.lastFlushAt = now;

  if (!session.entryRef) return;

  const updates: Record<string, unknown> = { lastSeen: serverTimestamp() };
  if (session.pendingCursor !== undefined) {
    updates.cursor = session.pendingCursor;
    session.pendingCursor = undefined;
  }
  if (session.pendingDragging !== undefined) {
    updates.dragging = session.pendingDragging;
    session.pendingDragging = undefined;
  }
  if (session.pendingSelection !== undefined) {
    updates.selection = session.pendingSelection;
    session.pendingSelection = undefined;
  }

  if (Object.keys(updates).length <= 1) return; // only lastSeen — skip
  update(session.entryRef, updates).catch((err) => {
    logWriteFailure("presence flush", err);
  });
}

/**
 * Surface RTDB write failures (rules denial, network) to devtools — but
 * only once per session, since the flush loop fires at ~30 Hz and a
 * persistent rules denial would otherwise spam the console.
 */
function logWriteFailure(stage: string, err: unknown): void {
  if (!session) return;
  if (session.warnedWriteFailed) return;
  session.warnedWriteFailed = true;
  console.error(
    `[presence] ${stage} failed for room "${session.roomId}" — ` +
      "likely a rules permission denial. Verify database.rules.json " +
      "is deployed and roomMembers/{roomId}/{uid} is mirrored.",
    err
  );
  captureException(err, {
    context: "presence.write",
    stage,
    roomId: session?.roomId,
  });
}

export function setCursor(cursor: PresenceCursor | null): void {
  if (!session) return;
  session.pendingCursor = cursor;
  scheduleFlush();
}

export function setSelection(sel: PresenceSelection | null): void {
  if (!session) return;
  session.pendingSelection = sel;
  scheduleFlush();
}

export function setDragging(d: PresenceDragging | null): void {
  if (!session) return;
  session.pendingDragging = d;
  scheduleFlush();
}

export function teardownPresence(): void {
  if (!session) return;
  if (session.entryRef) {
    void remove(session.entryRef).catch(() => {
      // Ignored — onDisconnect will clean up if this fails.
    });
  }
  session = null;
}

// ---------------------------------------------------------------------------
// Receiver — subscribe to presence for a room.
// ---------------------------------------------------------------------------

export interface PresenceSubscription {
  unsubscribe: () => void;
}

/**
 * Subscribe to all collaborators' presence for a room. The callback is
 * invoked with the aggregated `Map<uid, CollaboratorState>` whenever
 * anything changes (one entry added/removed, or a cursor/selection write).
 *
 * Multi-tab: when a user has multiple tabs open, the entry with the most
 * recent `lastSeen` wins — we don't render two cursors for one person.
 *
 * The local user's own uid is filtered out so the callback never returns
 * "yourself" — reduces consumer boilerplate.
 */
export function subscribePresence(
  roomId: string,
  selfUid: string,
  cb: (collaborators: Map<string, CollaboratorState>) => void
): PresenceSubscription {
  const db = getFirebaseDatabase();
  if (!db) return { unsubscribe: () => {} };

  const path = `presence/${roomId}`;
  const r = ref(db, path);
  // Map uid → tabId → entry; aggregated to a single CollaboratorState
  // per uid in `recompute()`.
  const tabsByUser = new Map<string, Map<string, PresenceEntry>>();

  function readEntry(snap: DataSnapshot): PresenceEntry | null {
    const v = snap.val() as Partial<PresenceEntry> | null;
    if (!v) return null;
    if (
      typeof v.uid !== "string" ||
      typeof v.tabId !== "string" ||
      typeof v.displayName !== "string" ||
      typeof v.color !== "string"
    ) {
      return null;
    }
    return {
      uid: v.uid,
      tabId: v.tabId,
      displayName: v.displayName,
      photoURL: v.photoURL ?? null,
      color: v.color,
      cursor: v.cursor ?? null,
      selection: v.selection ?? null,
      dragging: v.dragging ?? null,
      lastSeen: typeof v.lastSeen === "number" ? v.lastSeen : 0,
    };
  }

  function recompute(): void {
    const out = new Map<string, CollaboratorState>();
    for (const [uid, tabs] of tabsByUser.entries()) {
      if (uid === selfUid) continue;
      let best: PresenceEntry | null = null;
      for (const e of tabs.values()) {
        if (!best || e.lastSeen > best.lastSeen) best = e;
      }
      if (!best) continue;
      out.set(uid, {
        uid: best.uid,
        displayName: best.displayName,
        photoURL: best.photoURL,
        color: best.color,
        cursor: best.cursor,
        selection: best.selection,
        dragging: best.dragging,
        lastSeen: best.lastSeen,
      });
    }
    cb(out);
  }

  function ingest(uidSnap: DataSnapshot): void {
    const uid = uidSnap.key;
    if (!uid) return;
    const tabs = new Map<string, PresenceEntry>();
    uidSnap.forEach((tabSnap) => {
      const entry = readEntry(tabSnap);
      if (entry) tabs.set(tabSnap.key ?? entry.tabId, entry);
      return false;
    });
    if (tabs.size === 0) {
      tabsByUser.delete(uid);
    } else {
      tabsByUser.set(uid, tabs);
    }
  }

  // Use a single onValue on the room to keep the implementation simple
  // and re-aggregate on any change. Presence trees are tiny (1 entry per
  // user × tabs), so the cost is negligible compared to Firestore reads.
  const handler = onValue(
    r,
    (snap) => {
      tabsByUser.clear();
      snap.forEach((uidSnap) => {
        ingest(uidSnap);
        return false;
      });
      recompute();
    },
    (err) => {
      // Almost always a rules-level permission denial:
      //  (a) `database.rules.json` wasn't deployed (RTDB still on the
      //      project's default `.read: false` rules), or
      //  (b) the caller isn't in `roomMembers/{roomId}/{auth.uid}`
      //      because the share-callable's `syncMembersToRtdb` mirror
      //      didn't run (e.g. legacy rooms created pre-feature).
      // Log loudly so the cause is visible in devtools instead of
      // silently rendering "0 collaborators".
      console.error(
        `[presence] subscription failed for room "${roomId}" — ` +
          "likely a rules permission denial. Verify database.rules.json " +
          "is deployed and roomMembers/{roomId}/{uid} is mirrored.",
        err
      );
      captureException(err, { context: "presence.subscribe", roomId });
      tabsByUser.clear();
      cb(new Map());
    }
  );

  return {
    unsubscribe: () => {
      off(r, "value", handler);
    },
  };
}

// ---------------------------------------------------------------------------
// Test helpers
// ---------------------------------------------------------------------------

export function _resetPresenceForTests(): void {
  session = null;
}

/** Read-only view of the active presence session — for diagnostics. */
export function getActivePresenceRoomId(): string | null {
  return session?.roomId ?? null;
}

/** Whether a presence session is currently active for the local user. */
export function isPresenceActive(): boolean {
  return session?.initialized === true;
}
